import { reactive, toRefs } from 'vue';
import AiAgentBrainsAPI from 'dashboard/api/aiAgents/brains';

// D36: playground drawer state lives in a local composable, not Vuex.
// State is module-scoped per brain so the slide-over is PERSISTENT — the
// conversation survives tab switches inside the agent detail (each tab
// remounts AgentLayout and, with it, the drawer).
const states = new Map();

const buildState = () =>
  reactive({
    isOpen: false,
    isWaiting: false,
    messages: [],
    rendered: null,
    error: null, // { type: 'server' | 'unreachable', message }
    nextId: 1,
  });

// test-only helper: the per-brain map outlives a single component tree
export const clearPlaygroundStates = () => states.clear();

export function usePlayground(brainId) {
  const key = String(brainId);
  if (!states.has(key)) states.set(key, buildState());
  const state = states.get(key);

  // failed turns stay visible in the drawer but are never re-sent: the
  // runtime expects a clean alternating history of completed turns.
  const buildHistory = () =>
    state.messages
      .filter(message => !message.failed)
      .map(({ role, content }) => ({ role, content }));

  const sendMessage = async content => {
    const text = (content || '').trim();
    if (!text || state.isWaiting) return;

    const history = buildHistory();
    const userMessage = reactive({
      id: state.nextId,
      role: 'user',
      content: text,
      failed: false,
    });
    state.nextId += 1;
    state.messages.push(userMessage);
    state.isWaiting = true;
    state.error = null;

    try {
      const response = await AiAgentBrainsAPI.playground(key, {
        message: text,
        history,
      });
      const data = response.data || {};
      if (data.rendered) state.rendered = data.rendered;
      if (data.ok) {
        state.messages.push({
          id: state.nextId,
          role: 'assistant',
          content: data.reply,
          toolCalls: data.tool_calls || [],
        });
        state.nextId += 1;
      } else {
        userMessage.failed = true;
        state.error = { type: 'server', message: data.error || '' };
      }
    } catch (error) {
      // 503 = runtime not configured/unreachable (friendly state, keep the
      // conversation intact); other 4xx carry an { ok: false, error } body.
      userMessage.failed = true;
      const status = error.response?.status;
      const data = error.response?.data || {};
      if (data.rendered) state.rendered = data.rendered;
      const message = data.error || '';
      if (status && status !== 503 && message) {
        state.error = { type: 'server', message };
      } else {
        state.error = { type: 'unreachable', message };
      }
    } finally {
      state.isWaiting = false;
    }
  };

  const reset = () => {
    state.messages = [];
    state.rendered = null;
    state.error = null;
  };

  const open = () => {
    state.isOpen = true;
  };

  const close = () => {
    state.isOpen = false;
  };

  const toggle = () => {
    state.isOpen = !state.isOpen;
  };

  return {
    ...toRefs(state),
    sendMessage,
    reset,
    open,
    close,
    toggle,
  };
}
